import { useCallback } from 'react';
import { useModalNavigation } from '../modals/useModalNavigation';
import { useWalletsMe } from '../../api';
import { CoinType } from '../../models';
import { CommonModalsKey, WithdrawStep } from '../../enums';
import { useSearchParams } from '../useSearchParams';

export function useTransactionsNavigation() {
  // Retrieve the current wallet and account information
  const { wallet } = useWalletsMe();
  const currentAccount = wallet?.currentAccount;

  // Hook to navigate to different modals
  const { navigateToModal } = useModalNavigation();
  // Hook to manage URL search parameters
  const params = useSearchParams();

  /**
   * Constructs search parameters for a transaction modal.
   *
   * @param {CoinType} [coinType] - Optional type of the coin.
   * @param {bigint} [coinId] - Optional ID of the coin.
   * @returns {string} - A string representing the search parameters.
   *
   * Example:
   * If currentAccount has a coin type of 'BTC' and id of 123n,
   * getModalSearchParams() returns "?coin-type=BTC&coin-id=123&step=Form".
   */
  const getModalSearchParams = useCallback(
    (coinType?: CoinType, coinId?: bigint) => {
      // Set the coin type in the search parameters, defaulting to the current account's coin type
      params.set('coin-type', coinType ?? currentAccount?.coin?.type);
      // Set the coin ID in the search parameters, defaulting to the current account's coin ID
      params.set(
        'coin-id',
        coinId ? String(coinId) : String(currentAccount?.coin?.id),
      );
      // Set the step in the search parameters to 'Form'
      params.set('step', WithdrawStep.Form);

      // Return the constructed search parameters as a query string
      return `?${params.toString()}`;
    },
    [params, currentAccount],
  );

  /**
   * Navigates to a transaction modal with the appropriate search parameters.
   *
   * @param {CommonModalsKey.Deposit | CommonModalsKey.Withdraw} modal - The modal to navigate to.
   *
   * Example:
   * navigateToTransactionModal(CommonModalsKey.Deposit) will navigate to the deposit modal
   * with the search parameters constructed by getModalSearchParams().
   */
  const navigateToTransactionModal = useCallback(
    (modal: CommonModalsKey.Deposit | CommonModalsKey.Withdraw) => {
      // Get the search parameters for the modal
      const searchParams = getModalSearchParams();
      // Navigate to the specified modal with the search parameters
      navigateToModal(modal, searchParams);
    },
    [getModalSearchParams, navigateToModal],
  );

  // Return the functions to navigate to transaction modals and get search parameters
  return {
    navigateToTransactionModal,
    getModalSearchParams,
  };
}
